import { useState, useEffect, useContext } from "react"
import _ from "lodash"

// Context
import { UserContext } from "../../context/userContext"

//GraphQL
import { useLazyQuery, useQuery } from "@apollo/client"
import { GET_USERS_FROM_LIST, GET_AUTHORIZED_USER } from "./query"

//Referal modules
import {
  RefApiRefEntityNotesListGet,
  refApiRefEntityNotePut,
} from "./ReferralsAPI"
import { getLocalDateTimeFromIso, getNotePayload } from "./sharedFunctions"

//Style
import styles from "../../styles/Form-Inputs.module.css"
import refStyles from "../../styles/Referrals.module.css"
import refNoteStyles from "../../styles/ReferralNotes.module.css"
import loaderStyles from "../../styles/Loader.module.css"

//PrimeReact
import { Card } from "primereact/card"
import { Button } from "primereact/button"
import { ProgressSpinner } from "primereact/progressspinner"
import ReferralNoteModal from "./ReferralNoteModal"
import { LogError } from "../../helpers/logger"

export default function ReferralNotes(props) {
  const userCtx = useContext(UserContext)

  const referralId = props.referralId
  const referralEntityId = !!props.referralEntityId
    ? props.referralEntityId
    : undefined

  const referralCreatorUserId = props.referralCreatorStaffId || ""
  const referralOrganization = props.referralOrganizationID || ""

  const [errorMessage, setErrorMessage] = useState("")
  const [notes, setNotes] = useState([])
  const [createNewNote, setCreateNewNote] = useState(false)
  const [listUsers, setListUsers] = useState([
    {
      id: userCtx.participant_id,
      staff_id: undefined,
      participant_id: userCtx.participant_id,
      alias: userCtx.participant_alias,
      status: "",
      email: userCtx.participant_email_account,
      name2: `${userCtx.participant_name_first} ${userCtx.participant_name_last}`,
      userRole: userCtx.staff_role_name,
    },
  ])
  const [isLoading, setIsLoading] = useState(true)

  function logError(ex) {
    console.log(ex)

    if (ex.isAxiosError) {
      if (ex.response !== undefined) {
        setErrorMessage(
          "An error ocurred while retrieving the referral notes: " +
            ex.response.data,
        )
        LogError(ex)
      } else {
        setErrorMessage("Unable to load partner status: " + ex.message)
      }
    } else {
      setErrorMessage(ex)
    }
  }

  const [getParticipant] = useLazyQuery(GET_AUTHORIZED_USER, {
    fetchPolicy: "cache-and-network",
  })

  const { refetch: checkUserCache } = useQuery(GET_USERS_FROM_LIST, {
    fetchPolicy: "cache-and-network",
    skip: true,
    onError: (e) => {
      console.log(e)
      setErrorMessage(
        "Unable to retrieve user information, please refer to the console for more information",
      )
      setIsLoading(false)
    },
    onCompleted: async (data) => {
      console.log("in")
      if (data.users !== null) {
        let newUsers = data.users
          .filter((x) => x !== null)
          .map((x) => {
            return {
              id: x.staff_id,
              staff_id: x.staff_id,
              participant_id: undefined,
              alias: x.staff_alias,
              status: x.staff_user_status,
              email: x.staff_email_address,
              name2: `${x.staff_name_first} ${x.staff_name_last}`,
              userRole: x.staff_role_name,
            }
          })

        let missingUsers = notes.filter(
          (note) => !data.users.some((user) => user?.userId === note.userId),
        )

        if (missingUsers.length > 0) {
          //If there are still missing users, look for them in the participant query
          missingUsers = [...new Set(missingUsers.map((usr) => usr.userId))]

          let participants = await Promise.all(
            missingUsers.map(async (usr) => {
              return await getParticipant({
                variables: { participant_id: usr },
              })
            }),
          )

          participants = participants.filter((result) => !!result.data?.user)

          newUsers = [
            ...newUsers,
            ...participants.map((result) => {
              const participant = result.data.user
              return {
                id: participant.participant_id,
                staff_id: null,
                participant_id: participant.participant_id,
                alias: participant.participant_alias,
                status: "",
                email: participant.participant_email_account,
                name2: `${_.unescape(
                  participant.participant_name_first,
                )} ${_.unescape(participant.participant_name_last)}`,
                userRole: participant.staff_role_name,
              }
            }),
          ]
        }

        setListUsers([...listUsers, ...newUsers])
      }
      setIsLoading(false)
    },
  })

  useEffect(() => {
    setIsLoading(true)
    RefApiRefEntityNotesListGet(userCtx.tokens.getMainToken(), referralId)
      .then((result) => {
        try {
          if (result.length > 0) {
            if (result[0].referralId === null) {
              setNotes([])
              setIsLoading(false)
            } else {
              setNotes(result)
              checkUserCache({ list: result.map((note) => note.userId) })
              setIsLoading(false)
            }
          } else {
            setNotes(result)
            setIsLoading(false)
          }
        } catch (ex) {
          setNotes([])
          logError(exports)
          setIsLoading(false)
        }
      })
      .catch((ex) => {
        setNotes([])
        logError(ex)
        setIsLoading(false)
      })
  }, [checkUserCache, referralId])

  function getUser(id) {
    if (listUsers) {
      const usr = listUsers?.filter((x) => x.id === id)

      if (usr.length > 0) {
        return usr[0]
      } else {
        const listUsr = listUsers?.filter((x) => x.staff_id === id)

        if (listUsr.length > 0) {
          return listUsr[0]
        } else {
          return null
        }
      }
    } else {
      return null
    }
  }

  function getNoteUserDisplay(note) {
    let userDisplay = undefined
    let profileEmail = undefined
    let profileName = undefined

    if (!!note.notePayload) {
      try {
        let payload = JSON.parse(note.notePayload)
        profileEmail = payload.email
        profileName = payload.displayName
      } catch {
        userDisplay = undefined
      }
    }

    if (!profileName) {
      let userProfile = getUser(note.userId)
      if (!!userProfile) {
        profileEmail = userProfile.email
        profileName = userProfile.name2
      }
    }

    userDisplay = !!profileName
      ? `${profileName} [${profileEmail}]`
      : "Unknown user" + (!!profileEmail ? ` [${profileEmail}]` : "")

    return userDisplay
  }

  function GetNote(note) {
    let entityStatus = undefined
    let referralStatus = undefined
    let bodyText = note.note

    const getStatusText = (oldStatus, newStatus) => {
      return (
        <span
          className={`${refNoteStyles.status} ${refNoteStyles.noteEntryLabel}`}
        >
          {!!oldStatus ? oldStatus : "<Undefined>"}
          <i className="pi pi-angle-right"></i>
          {!!newStatus ? newStatus : "<Undefined>"}
        </span>
      )
    }

    if (!!note.referralStatusOld || !!note.referralStatusNew) {
      referralStatus = getStatusText(
        note.referralStatusOld,
        note.referralStatusNew,
      )
    }

    if (!!note.referralEntityStatusOld || !!note.referralEntityStatusNew) {
      entityStatus = getStatusText(
        note.referralEntityStatusOld,
        note.referralEntityStatusNew,
      )
    } else if (
      !!bodyText &&
      bodyText.startsWith("[Status update: ") &&
      bodyText.includes("] : ")
    ) {
      bodyText = note.note.replace("[Status update: ", "")
      entityStatus = bodyText.substring(0, bodyText.indexOf("] : "))
      bodyText = bodyText.substring(bodyText.indexOf("] : ") + 4)
    }

    return (
      <div className={refNoteStyles.note} key={note.creationDate}>
        <div className={refNoteStyles.noteLine}>
          <div className={refNoteStyles.noteItem}>
            <span className={refNoteStyles.noteEntryLabelTitle}>
              Author:{" "}
              <span className={refNoteStyles.noteEntryLabel}>
                {getNoteUserDisplay(note)}
              </span>
            </span>
          </div>
          <div className={refNoteStyles.noteItem}>
            <span className={refNoteStyles.noteEntryLabelTitle}>
              Created:{" "}
              <span className={refNoteStyles.noteEntryLabel}>
                {getLocalDateTimeFromIso(note.creationDate)}
              </span>
            </span>
          </div>
        </div>
        {!!referralStatus ? (
          <div className={refNoteStyles.noteLine}>
            <div className={refNoteStyles.noteItem}>
              <span className={refNoteStyles.noteEntryLabelTitle}>
                Referral status update: {referralStatus}
              </span>
            </div>
          </div>
        ) : null}
        {!!entityStatus ? (
          <div className={refNoteStyles.noteLine}>
            <div className={refNoteStyles.noteItem}>
              <span className={refNoteStyles.noteEntryLabelTitle}>
                Referral partner status update: {entityStatus}
              </span>
            </div>
          </div>
        ) : null}
        {bodyText !== "" ? (
          <div className={refNoteStyles.noteLine}>
            <div className={refNoteStyles.noteItem}>
              <span className={refNoteStyles.noteEntryLabelTitle}>
                Note Summary:{" "}
                <span className={refNoteStyles.noteEntryLabel}>{bodyText}</span>
              </span>
            </div>
          </div>
        ) : null}
      </div>
    )
  }

  function handleSubmit(values) {
    refApiRefEntityNotePut(userCtx.tokens.getMainToken(), referralId, values)
      .then((result) => {
        let creationDate = new Date()
        values.creationDate = creationDate.toISOString()
        values.userId = userCtx.participant_id
        notes.unshift(values)
        setCreateNewNote(false)
      })
      .catch((ex) => {
        logError(ex)
      })
  }

  if (isLoading) {
    return (
      <div className={loaderStyles.wrapperMed}>
        <ProgressSpinner />
      </div>
    )
  }
  return (
    <>
      <ReferralNoteModal
        notePayload={getNotePayload(userCtx)}
        handleSubmit={handleSubmit}
        createNewNote={createNewNote}
        participantId={userCtx.participant_id}
        referralCreatorUserId={referralCreatorUserId}
        referralEntityId={referralEntityId}
        referralId={referralId}
        setCreateNewNote={setCreateNewNote}
        referralOrganization={referralOrganization}
      />
      <div className={refStyles.notesHeader}>
        <Button
          label="New Note"
          type="Button"
          onClick={() => setCreateNewNote(true)}
          className={refStyles.newNoteButton}
        />
      </div>
      <div>
        {errorMessage !== "" ? (
          <label className={styles.errFieldMsg}>{errorMessage}</label>
        ) : null}
      </div>
      <div className={refNoteStyles.notesContainer}>
        {notes?.length > 0 ? (
          notes?.map((note) => GetNote(note))
        ) : (
          <Card>No notes available</Card>
        )}
      </div>
    </>
  )
}
